import { Injectable, NgZone } from '@angular/core';
import { AuthService } from './auth.service';
import { TokenStorageService } from './token-storage.service';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/**
 * Monitors user activity (mouse, key, scroll, touch) and logs out after a period of inactivity.
 * Requirement: auto logout after 2 full hours (7200000 ms) of no activity.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private static readonly IDLE_LIMIT_MS = 2 * 60 * 60 * 1000; // 2 hours
  private activitySub?: Subscription;
  private idleTimerSub?: Subscription;

  constructor(
    private zone: NgZone,
    private authService: AuthService,
    private tokenStorage: TokenStorageService,
  ) {
    this.startMonitoring();
  }

  private startMonitoring() {
    this.zone.runOutsideAngular(() => {
      const activityEvents = [
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'keydown'),
        fromEvent(window, 'wheel'),
        fromEvent(window, 'touchstart'),
        fromEvent(window, 'click'),
        fromEvent(window, 'scroll'),
      ];
      const merged = merge(...activityEvents).pipe(debounceTime(400));
      this.activitySub = merged.subscribe(() => this.resetTimerIfLoggedIn());
      this.resetTimerIfLoggedIn();
    });
  }

  private resetTimerIfLoggedIn() {
    if (!this.tokenStorage.getToken()) {
      this.clearTimer();
      return;
    }
    // Update persistent last activity timestamp on any detected activity
    this.tokenStorage.updateActivity();
    this.clearTimer();
    this.idleTimerSub = timer(IdleTimeoutService.IDLE_LIMIT_MS).subscribe(() => {
      // Run logout inside Angular zone to trigger navigation / change detection
      this.zone.run(() => {
        if (this.tokenStorage.getToken() && this.tokenStorage.isExpired()) {
          this.authService.logout();
        }
      });
    });
  }

  private clearTimer() {
    if (this.idleTimerSub) {
      this.idleTimerSub.unsubscribe();
      this.idleTimerSub = undefined;
    }
  }

  stop() {
    this.activitySub?.unsubscribe();
    this.clearTimer();
  }
}
